import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import './App.css';
import profilePic from './images/pfp.jpg';
import resumePDF from './Mohammed_Zaid_Resume.pdf';
import ucsdImg from './images/ucsdwebpic.jpg'; 

function App() {
 const [activeSection, setActiveSection] = useState('about');
 const controls = useAnimation();
 
 useEffect(() => {
 controls.start({
 y: [0, -10, 0],
 transition: { 
 repeat: Infinity, 
 duration: 2,
 ease: "easeInOut"
 }
 });
 }, [controls]);
 

 const pageTransition = {
 hidden: { opacity: 0, y: 20 },
 visible: { 
 opacity: 1, 
 y: 0,
 transition: { 
 duration: 0.7,
 ease: [0.6, -0.05, 0.01, 0.99]
 }
 },
 exit: { 
 opacity: 0, 
 y: -20,
 transition: { duration: 0.5 }
 }
 };

 const staggerContainer = {
 hidden: { opacity: 0 },
 visible: {
 opacity: 1,
 transition: {
 staggerChildren: 0.1,
 delayChildren: 0.3
 }
 }
 };

 const staggerItem = {
 hidden: { y: 20, opacity: 0 },
 visible: {
 y: 0,
 opacity: 1,
 transition: {
 type: "spring",
 stiffness: 100
 }
 }
 };

const heroBio =
  "Hey! I'm Mohammed Y. Zaid, a Mathematics and Computer Science senior at UC San Diego, turning real-world problems into thoughtful, scalable solutions through code.";

 return (
 <div
  className="App"
  onMouseMove={(event) => {
    event.currentTarget.style.setProperty(
      "--mouse-x",
      `${event.clientX}px`
    );

    event.currentTarget.style.setProperty(
      "--mouse-y",
      `${event.clientY}px`
    );
  }}
>
<div className="cursor-glow" aria-hidden="true"></div>
 <div className="background-shapes">
 <div className="shape shape-1"></div>
 <div className="shape shape-2"></div>
 <div className="shape shape-3"></div>
 <div className="shape shape-4"></div>
 </div>
 
 <nav>
 <motion.div 
 className="name"
 initial={{ opacity: 0, y: -20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.5 }}
 whileHover={{ scale: 1.05 }}
 >
 Mohammed Y. Zaid
 </motion.div>
 <motion.ul
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.3, staggerChildren: 0.1 }}
 >
 <motion.li 
 className={activeSection === 'about' ? 'active' : ''}
 whileHover={{ scale: 1.05, color: "#e0ead6" }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('about')}
 >
 About
 </motion.li>
 <motion.li 
 className={activeSection === 'experience' ? 'active' : ''}
 whileHover={{ scale: 1.05, color: "#e0ead6" }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('experience')}
 >
 Experience
 </motion.li>
 <motion.li 
 className={activeSection === 'education' ? 'active' : ''}
 whileHover={{ scale: 1.05, color: "#e0ead6" }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('education')}
 >
 Education
 </motion.li>
 <motion.li 
 className={activeSection === 'projects' ? 'active' : ''}
 whileHover={{ scale: 1.05, color: "#e0ead6" }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('projects')}
 >
 Projects
 </motion.li>
 <motion.li 
 className={activeSection === 'skills' ? 'active' : ''}
 whileHover={{ scale: 1.05, color: "#e0ead6" }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('skills')}
 >
 Skills
 </motion.li>
 </motion.ul>
 </nav>

 <main>
 <AnimatePresence mode="wait">
 {activeSection === 'about' && (
 <motion.section 
 key="about"
 variants={pageTransition}
 initial="hidden"
 animate="visible"
 exit="exit"
 className="about-section"
 >
<motion.div
  className="profile-container"
  whileHover={{
    scale: 1.05
  }}
  transition={{
    type: "spring",
    stiffness: 220,
    damping: 18
  }}
>
  <motion.div
    className="profile-ring"
    animate={{
      rotate: 360
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      ease: "linear"
    }}
  />

  <motion.img
    src={profilePic}
    alt="Mohammed Y. Zaid"
    className="profile-pic"
    initial={{
      opacity: 0,
      scale: 0.8
    }}
    animate={{
      opacity: 1,
      scale: 1,
      rotate: [0, 5, 0, -5, 0]
    }}
    transition={{
      duration: 1.2,
      rotate: {
        repeat: 0,
        duration: 1.5
      }
    }}
    whileHover={{
      boxShadow: "0 8px 25px rgba(0,0,0,0.25)"
    }}
  />
</motion.div>

 <motion.div
 className="about-content"
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.3, duration: 0.8 }}
 >
 <h2 className="hero-title">
 <motion.span
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 0.2, duration: 0.8 }}
 >
 About Me
 </motion.span>
 </h2>

 <motion.div 
 className="contact-info"
 variants={staggerContainer}
 initial="hidden"
 animate="visible"
 >
 <motion.p variants={staggerItem}>
  <a
    href="mailto:mdyasinzaidabir@gmail.com"
    className="email-link"
  >
    mdyasinzaidabir@gmail.com
  </a>
  {" | San Diego, CA"}
</motion.p>
 <motion.p variants={staggerItem}>US Citizen</motion.p>

 <motion.p variants={staggerItem}>
 <a href="https://www.linkedin.com/in/mohammed-zaid-304a9b279/" target="_blank" rel="noopener noreferrer">LinkedIn</a> | 
 <a href="https://github.com/mzaid03" target="_blank" rel="noopener noreferrer"> GitHub</a> 
 </motion.p>
 </motion.div>

<motion.p
  className="bio"
  initial="hidden"
  animate="visible"
  variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.07,
        delayChildren: 0.8
      }
    }
  }}
>
  {heroBio.split(" ").map((word, index) => (
    <motion.span
      key={index}
      style={{
        display: "inline-block",
        marginRight: "0.3rem",
        willChange: "opacity, transform, filter, text-shadow"
      }}
      variants={{
        hidden: {
          opacity: 0,
          y: 15,
          filter: "blur(7px)",
          textShadow: "0 0 0px rgba(64, 145, 108, 0)"
        },
        visible: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          textShadow: [
            "0 0 0px rgba(64, 145, 108, 0)",
            "0 0 18px rgba(64, 145, 108, 0.9)",
            "0 0 7px rgba(64, 145, 108, 0.3)"
          ],
          transition: {
            duration: 0.7,
            ease: "easeOut"
          }
        }
      }}
    >
      {word}
    </motion.span>
  ))}
</motion.p>

 <motion.div 
 className="cta-container"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.8, duration: 0.8 }}
 >
 <motion.button 
 className="cta-button"
 whileHover={{ 
 scale: 1.05, 
 backgroundColor: "#1e3a29",
 boxShadow: "0 5px 15px rgba(0,0,0,0.2)"
 }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('experience')}
 >
 View Experience
 </motion.button>
 <motion.button 
 className="cta-button secondary"
 whileHover={{ 
 scale: 1.05,
 backgroundColor: "#e9f0e3",
 boxShadow: "0 5px 15px rgba(0,0,0,0.1)"
 }}
 whileTap={{ scale: 0.95 }}
 onClick={() => setActiveSection('projects')}
 >
 See Projects
 </motion.button>
 <motion.a 
 href={resumePDF}
 target="_blank"
 rel="noopener noreferrer"
 className="cta-button resume-button"
 whileHover={{ 
 scale: 1.05,
 boxShadow: "0 5px 15px rgba(0,0,0,0.1)"
 }}
 whileTap={{ scale: 0.95 }}
 >
 View Resume
 </motion.a>
 </motion.div>
 </motion.div>
 </motion.section>
 )}

 {activeSection === 'experience' && (
  <motion.section
    key="experience"
    variants={pageTransition}
    initial="hidden"
    animate="visible"
    exit="exit"
    className="experience-section"
  >
    <motion.h2
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
    Experience
    </motion.h2>

    <motion.div
      className="experience-item card-effect"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.7 }}
      whileHover={{
        y: -10,
        boxShadow: "0 15px 30px rgba(0,0,0,0.15)"
      }}
    >
      <div className="experience-header">
        <h3>Maroon Compatibility Group Inc.</h3>
        <p className="position">Software Engineer Intern</p>
        <p className="location-date">
          Lake Elsinore, CA | June 2026 - July 2026
        </p>
      </div>

      <ul>
        <li>Developed 15 scalable features using React, TypeScript, and REST APIs, improving user engagement by 30%.</li>
        <li>Debugged 30+ production issues using Agile development practices, increasing application reliability.</li>
        <li>Built 10+ production UI components, reducing development time by 35% and improving code maintainability.</li>
        <li>Reduced Front-End latency by 25% by optimizing component rendering and API integration, improving the app.</li>
      </ul>

      <div className="experience-header">
        <h3>Freelance</h3>
        <p className="position">Software Engineer</p>
        <p className="location-date">
          Lake Elsinore, CA | April 2025 - Present
        </p>
      </div>

      <ul>
        <li>Developed 20 responsive websites via HTML5, CSS3, and JavaScript, for owners to establish an online presence.</li>
        <li>Architected polished brand interfaces using Flexbox, CSS Grid, and custom animations, improving usability.</li>
        <li>Implemented responsive navigation, smooth-scroll routing, and menu features for a seamless user experience.</li>
        <li>Collaborated with the client to gather feedback and refine site features based on evolving business needs.</li>
      </ul>

      <div className="experience-header">
        <h3>Muslim Tech Collaborative</h3>
        <p className="position">Member</p>
        <p className="location-date">
          La Jolla, CA | April 2024 - Present
        </p>
      </div>

      <ul>
        <li>Constructed 10+ full-stack apps using React/Node.js, applying agile sprints to boost member skills.</li>
        <li>Developed ML pipelines with TensorFlow, streamlining preprocessing and training to reach 99% accuracy.</li>
        <li>Led more than 30 collaborative projects, enforcing Git/Agile practices to cut delivery time by 35%.</li>
        <li>Facilitated 10+ resume workshops, creating ATS-ready templates that improved internship callbacks by 30%.</li>
      </ul>

      <div className="experience-header">
        <h3>UCSD Associated Students</h3>
        <p className="position">Campus Wide Senator</p>
        <p className="location-date">
          La Jolla, CA | April 2025 - September 2025
        </p>
      </div>

      <ul>
        <li>Strategically managed an $8 million operational budget, implementing rigorous oversight processes and developing targeted funding initiatives that efficiently allocated resources across campus priorities</li>
        <li>Orchestrated management systems for 600+ student organizations, designing streamlined approval processes and resource allocation protocols that enhanced club operations and campus engagement</li>
        <li>Advocated for comprehensive student needs through data driven policy development, collaborating with administration to implement solutions that measurably improved campus experience and services</li>
        <li>Executed critical departmental decisions through structured analysis frameworks, ensuring alignment with strategic objectives while maintaining transparent communication with stakeholders</li>
      </ul>

      <div className="experience-header">
        <h3>UCSD Information Desk</h3>
        <p className="position">Student Administrative Support</p>
        <p className="location-date">
          La Jolla, CA | September 2024 - Present
        </p>
      </div>

      <ul>
        <li>Orchestrated real-time resource allocation system for optimizing student workflow efficiency, implementing data-driven scheduling algorithms that reduced station transition times by 15%</li>
        <li>Developed and executed escalation protocols for priority customer requests, implementing a structured methodology that ensured 99% on-time delivery of complex orders while maintaining quality standards</li>
      </ul>

      <div className="experience-header">
        <h3>VEX Robotics</h3>
        <p className="position">Founder &amp; President</p>
        <p className="location-date">
          Lake Elsinore, CA | August 2021 - June 2022
        </p>
      </div>

      <ul>
        <li>Programmed autonomous object manipulation algorithms in C for the team's robot, serving as lead driver during testing, optimizing system performance through iterative debugging and real-time adjustments</li>
        <li>Architected the robot's control systems using object-oriented design principles, developed comprehensive technical documentation including UML diagrams, and system architecture specifications</li>
        <li>Pioneered school's first-ever finals appearance in VEX Robotics competition, implementing advanced MatLab simulations for motion planning and mechanical stress analysis that secured 2nd place overall</li>
      </ul>
    </motion.div>
  </motion.section>
)}

 {activeSection === 'education' && (
 <motion.section 
 key="education"
 variants={pageTransition}
 initial="hidden"
 animate="visible"
 exit="exit"
 className="education-section"
 >
 <motion.h2
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.5 }}
 >
 Education
 </motion.h2>
 <motion.div 
 className="education-item card-effect"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3, duration: 0.7 }}
 >
 <div className="education-header">
 <h3>University of California, San Diego</h3>
 <p className="degree">B.S. - Computer Science & Mathematics</p>
 <p className="date">June 2026</p>
 </div>
 <motion.div 
 className="coursework"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5, duration: 0.7 }}
 >
 <h4>Relevant Coursework:</h4>
 <p>Advanced Data Structures, Software Engineering, Supervised Machine Learning, Components and Design Techniques for Digital Systems, Foundations in Numerical Analysis, Discrete Mathematics</p>
 </motion.div>
 </motion.div>
 <motion.div 
 className="education-decoration"
 initial={{ opacity: 0, scale: 0.8 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.7, duration: 0.5 }}
 >
<motion.div className="edu-icon" animate={controls}>
  <img src={ucsdImg} alt="UCSD" className="edu-img" />
</motion.div>
 </motion.div>
 </motion.section>
 )}

 {activeSection === 'projects' && (
 <motion.section 
 key="projects"
 variants={pageTransition}
 initial="hidden"
 animate="visible"
 exit="exit"
 className="projects-section"
 >
 <motion.h2
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ duration: 0.5 }}
 >
 
 Projects
 </motion.h2>
 <motion.div 
 className="project-item card-effect"
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.3, duration: 0.7 }}
 whileHover={{ y: -10, boxShadow: "0 15px 30px rgba(0,0,0,0.15)" }}
 >
 <h3>Huffman Code Decypher</h3>
 <p className="tech-stack">C, Data Structures, Algorithms, Binary Trees</p>
 <ul>
 <li>Architected a high-performance computational pattern recognition system utilizing Huffman tree algorithms in C, implementing optimized binary encoding techniques to achieve sub-linear time complexity for decryption operations while maintaining O(log n) space efficiency</li>
 </ul>
 <h3>FIFA Rankings & Political Corruption Analysis</h3>
 <p className="tech-stack">Python, Data Analysis, Pandas, Matplotlib, Statistical Modeling, Data Visualization</p>
 <ul>
 <li>Developed a comprehensive data science project analyzing the correlation between political corruption, press freedom, and national football performance by leveraging FIFA rankings, CPI, and GDP data; implemented statistical analysis and dynamic visualizations in Python to uncover patterns of institutional influence and sportswashing in international tournaments.</li>
 </ul>
 <h3>Islamic Prayer Reminder Website</h3>
 <p className="tech-stack">HTML, CSS, JavaScript, Aladhan API, Open-Meteo API, Web UI/UX</p>
 <ul>
 <li>Designed and implemented a location-based Islamic prayer reminder web application that dynamically displays daily Salah times, Hijri dates, Quranic verses by integrating the Aladhan and Open-Meteo geolocation APIs; enabled both auto-location and manual ZIP/city entry for flexible user access across regions. Engineered a visually appealing and mobile-responsive interface using modern JavaScript, HTML, and CSS, with advanced features such as animated Dhikr page titles, interactive Hadith refresh, dark mode toggling, and an embedded PDF guide for learning how to pray, prioritizing both accessibility and spiritual enrichment through thoughtful design and real-time religious functionality.</li>
 </ul>
 <h3>IoT Botnet Detection System</h3>
 <p className="tech-stack">Python, NumPy, Pandas, Scikit-learn, Matplotlib, Seaborn, Machine Learning</p>
 <ul>
 <li>Designed and implemented a large-scale machine learning pipeline to detect IoT botnet intrusions using the UCI N-BaIoT dataset (7M+ records, 115 engineered features across 9 IoT devices and 8 attack types). Executed comprehensive preprocessing and eliminating 157k+ duplicates, addressing a 92.1% class imbalance through undersampling, and applying Min-Max normalization to ensure statistical robustness. Trained and optimized a Decision Tree classifier (depth=6, 500 samples/leaf) that achieved 99.96% accuracy, precision, recall, and F1-score on a 205k-sample test set with only 86 misclassifications, validated through correlation heatmap analysis and error diagnostics. Positioned the pipeline for scalability by proposing Support Vector Machines for enhanced generalization in high-dimensional feature spaces, demonstrating proficiency in big-data analytics, supervised learning, and applied cybersecurity modeling.</li>
 </ul>
 <h3>SyncDocs</h3>
 <p className="tech-stack">React, TipTap, Node.js, Yjs, WebSockets, HTML, CSS, JSON, TypeScript, npm, Docker, Vite</p>
 <ul>
 <li>Enabled seamless editing for 100+ users through React, TipTap, Node.js, and Yjs with sub-100ms latency, while implementing scalable collaboration via Redis-backed presence to ensure multi-user synchronization. Enhanced usability with a dark-mode UI and CI/CD pipelines that streamlined deployment processes, and achieved local accessibility by designing containerized microservices within a cloud-native architecture.</li>
 </ul>
 <h3>Tasklee</h3>
 <p className="tech-stack">React, Next.js, TypeScript, Supabase Authentication (Anonymous), PostgreSQL Database, Row-Level Security, Tailwind CSS, REST APIs, Git/GitHub, Vercel</p>
 <ul>
 <li>Built a multi-tenant task management system leveraging Next.js and Supabase, achieving full tenant isolation through anonymous authentication and row-level security. Designed a PostgreSQL-backed architecture to ensure persistent data storage across user sessions. Developed a responsive, type-safe interface using TypeScript, incorporating smooth loading states and robust error handling to enhance user experience. Additionally, implemented real-time metrics, session reset functionality, and clear system feedback to support usability, reliability, and effective product demonstrations.</li>
 </ul>
 <h3>Velix</h3>
 <p className="tech-stack">Next.js, React, TypeScript, Supabase, PostgreSQL, Row-Level Security, Real-Time Subscriptions, Tailwind CSS, Git/GitHub, Vercel, GenAI Tooling</p>
 <ul>
 <li>Developed and deployed a real-time messaging application using Next.js and TypeScript, enabling seamless and reliable communication between users. Designed a secure PostgreSQL schema in Supabase with row-level security to ensure that only authorized members could access messages. Engineered low-latency message delivery for instant synchronization and consistent state across clients. Additionally, integrated GenAI-assisted development tools to streamline workflows, automate repetitive tasks, and improve overall development efficiency.</li>
 </ul>
 <h3>GlucoRisk</h3>
 <p className="tech-stack">Python • FastAPI • Scikit-learn • Pandas • NumPy • Next.js • React • TypeScript • Tailwind CSS • Pytest • Vercel • Render</p>
 <ul>
 <li>GlucoRisk is a publicly accessible, explainable machine-learning platform that provides educational elevated-A1C screening estimates using nine non-laboratory health and lifestyle factors. I architected a modular ETL and feature-engineering pipeline that transformed eight CDC NHANES 2021–2023 datasets into a reproducible cohort of 5,181 participants while improving physical-activity data completeness by 97.9%, reducing missing records from 3,364 to 71 through SAS XPT zero-decoding and temporal-unit normalization. To prioritize identifying individuals who may benefit from professional screening, I trained a survey-weighted Logistic Regression pipeline with automated imputation, feature scaling, and validation-based threshold optimization, identifying 198 of 234 elevated-A1C cases and achieving 84.6% recall, 0.739 ROC-AUC, and only 36 false negatives across 778 held-out test samples. I further improved model reliability by reducing the Brier score by 13.8% compared with the prevalence baseline and auditing performance across eight demographic subgroups. Finally, I deployed the versioned model through a FastAPI REST service connected to a type-safe Next.js and TypeScript frontend, achieving a 100% pass rate across six automated API tests with Pydantic validation, configurable CORS, health monitoring, and automated Render and Vercel deployments. The platform helps users better understand when professional A1C screening may be appropriate while clearly presenting itself as an educational tool rather than a medical diagnosis.</li>
 </ul>
 <div className="project-visual">
 <div className="binary-tree">
 <div className="node root">W</div>
 <div className="branches level-1">
 <div className="branch left">
 <div className="node">S</div>
 </div>
 <div className="branch right">
 <div className="node">E</div>
 </div>
 </div>
 </div>
 </div>
 </motion.div>
 </motion.section>
 )}

 

{activeSection === 'skills' && (
  <motion.section
    key="skills"
    variants={pageTransition}
    initial="hidden"
    animate="visible"
    exit="exit"
    className="skills-section"
  >
    <motion.h2
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
    >
      Technical Skills
    </motion.h2>

    <motion.div
      className="skills-container"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Programming Languages */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Programming Languages</h3>

        <div className="skills-badges">
          <span className="skill-badge">Java</span>
          <span className="skill-badge">Python</span>
          <span className="skill-badge">JavaScript</span>
          <span className="skill-badge">TypeScript</span>
          <span className="skill-badge">C++</span>
          <span className="skill-badge">C</span>
          <span className="skill-badge">Go</span>
          <span className="skill-badge">MATLAB</span>
        </div>
      </motion.div>

      {/* Frameworks, Libraries, and Tools */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Frameworks, Libraries & Tools</h3>

        <div className="skills-badges">
          <span className="skill-badge">React</span>
          <span className="skill-badge">Next.js</span>
          <span className="skill-badge">Node.js</span>
          <span className="skill-badge">Tailwind CSS</span>
          <span className="skill-badge">Redis</span>
          <span className="skill-badge">Prisma</span>
          <span className="skill-badge">TensorFlow</span>
          <span className="skill-badge">Scikit-learn</span>
          <span className="skill-badge">Pandas</span>
          <span className="skill-badge">NumPy</span>
          <span className="skill-badge">Firebase</span>
          <span className="skill-badge">Git</span>
          <span className="skill-badge">Figma</span>
          <span className="skill-badge">HTML</span>
          <span className="skill-badge">CSS</span>
          <span className="skill-badge">Leetcode</span>
        </div>
      </motion.div>

      {/* Cloud and DevOps */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Cloud & DevOps</h3>

        <div className="skills-badges">
          <span className="skill-badge">AWS</span>
          <span className="skill-badge">AWS EC2</span>
          <span className="skill-badge">AWS S3</span>
          <span className="skill-badge">AWS Lambda</span>
          <span className="skill-badge">Docker</span>
          <span className="skill-badge">CI/CD Pipelines</span>
          <span className="skill-badge">Linux</span>
          <span className="skill-badge">Microservices</span>
          <span className="skill-badge">Railway</span>
        </div>
      </motion.div>

      {/* Databases */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Databases</h3>

        <div className="skills-badges">
          <span className="skill-badge">PostgreSQL</span>
          <span className="skill-badge">SQL</span>
          <span className="skill-badge">NoSQL</span>
        </div>
      </motion.div>

      {/* Development Practices */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Development Practices</h3>

        <div className="skills-badges">
          <span className="skill-badge">Agile</span>
          <span className="skill-badge">SDLC</span>
          <span className="skill-badge">
            Debugging & Troubleshooting
          </span>
          <span className="skill-badge">Distributed Systems</span>
          <span className="skill-badge">
            Operational Excellence
          </span>
        </div>
      </motion.div>

      {/* Managerial Skills */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Managerial Skills</h3>

        <div className="skills-badges">
          <span className="skill-badge">Project management</span>
          <span className="skill-badge">Team leadership</span>
          <span className="skill-badge">
            Workflow optimization
          </span>
          <span className="skill-badge">Strategic thinking</span>
          <span className="skill-badge">UX design</span>
          <span className="skill-badge">Accessibility</span>
          <span className="skill-badge">Quality assurance</span>
          <span className="skill-badge">Version control</span>
          <span className="skill-badge">Data integrity</span>
        </div>
      </motion.div>

      {/* Certifications */}
      <motion.div
        className="skills-group card-effect"
        variants={staggerItem}
      >
        <h3>Certifications</h3>

        <div className="skills-badges">
          <span className="skill-badge">
            HackerRank Advanced Python
          </span>
          <span className="skill-badge">
            HackerRank Advanced SQL
          </span>
          <span className="skill-badge">
            HackerRank Advanced Go
          </span>
          <span className="skill-badge">
            HackerRank Advanced JavaScript
          </span>
        </div>
      </motion.div>
    </motion.div>
  </motion.section>
)}
 </AnimatePresence>
 </main>

 <motion.footer
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 transition={{ delay: 1 }}
 >
 <p>© {new Date().getFullYear()} Mohammed Y. Zaid. All rights reserved.</p>
 </motion.footer>
 </div>
 );
}

export default App;